import { useEffect, useState } from "react"
import { GetTournamentInfoMatches } from "../repo/TournamentsRepo.js"
import { LongDateFormat } from '../utils/date_format.js'

const parseStart = (startsAt) => new Date(startsAt.endsWith("Z") ? startsAt : startsAt + "Z")

function defaultPhaseIndex(data) {
  const phases = Object.entries(data)
    .sort(([, a], [, b]) => new Date(a[0].starts_at) - new Date(b[0].starts_at))

  if (phases.length === 0) {
    return 0
  }

  const now = new Date()
  let bestIndex = -1
  let bestStart = null

  phases.forEach(([, matches], index) => {
    matches.forEach((match) => {
      const start = parseStart(match.starts_at)
      if (start > now && (bestStart === null || start < bestStart)) {
        bestStart = start
        bestIndex = index
      }
    })
  })

  return bestIndex === -1 ? phases.length - 1 : bestIndex
}

export default function TournamentInfoMatches({tournamentId}) {
  const [tournamentMatches, setTournamentMatches] = useState(null)
  const [phaseIndex, setPhaseIndex] = useState(0)
  const defaultBadgeThumb = "https://bolaodacopa.com/images/default_badge_thumb.png"

  useEffect(() => {
    async function fetchTournament() {
      try {
        const tournamentInfo = await GetTournamentInfoMatches(tournamentId)
        const data = tournamentInfo["data"]
        setTournamentMatches(data)
        setPhaseIndex(defaultPhaseIndex(data))
      } catch (error) {
        console.error('Error fetching tournament info:', error)
      }
    }

    if (tournamentId) {
      fetchTournament()
    }
  }, [tournamentId])

  if (!tournamentMatches) {
    return null
  }

  const orderedPhases = Object.entries(tournamentMatches)
    .sort(([, a], [, b]) => new Date(a[0].starts_at) - new Date(b[0].starts_at))

  if (orderedPhases.length === 0) {
    return null
  }

  const safePhaseIndex = Math.min(phaseIndex, orderedPhases.length - 1)
  const [phase, matches] = orderedPhases[safePhaseIndex]
  const isFirst = safePhaseIndex === 0
  const isLast = safePhaseIndex === orderedPhases.length - 1

  return (
    <div className="mb-4 bg-slate-100 p-4 rounded-lg">
      <div className="flex flex-row items-center justify-between border-b py-2 mb-2 bg-white">
        <button
          type="button"
          onClick={() => setPhaseIndex(safePhaseIndex - 1)}
          disabled={isFirst}
          className={`px-2 text-6xl ${isFirst ? "text-slate-300 cursor-not-allowed" : "text-green-600 hover:text-green-800"}`}
          aria-label="Fase anterior"
        >
          &lsaquo;
        </button>
        <h3 className="text-md font-semibold uppercase text-center flex-1">{ phase }</h3>
        <button
          type="button"
          onClick={() => setPhaseIndex(safePhaseIndex + 1)}
          disabled={isLast}
          className={`px-2 text-6xl ${isLast ? "text-slate-300 cursor-not-allowed" : "text-green-500 hover:text-green-800"}`}
          aria-label="Próxima fase"
        >
          &rsaquo;
        </button>
      </div>

      <div className="flex flex-col gap-4">
        {matches.map((match) => {
          const matchStart = new Date(match.starts_at.endsWith("Z") ? match.starts_at : match.starts_at + "Z")
          const isLive = !match.is_ended && matchStart <= new Date()
          const aWins = match.is_ended && match.team_a_score > match.team_b_score
          const bWins = match.is_ended && match.team_b_score > match.team_a_score
          const showScores = isLive || match.is_ended
          const teamAScore = match.team_a_score == null ? (isLive ? 0 : null) : match.team_a_score
          const teamBScore = match.team_b_score == null ? (isLive ? 0 : null) : match.team_b_score

          return (
            <div key={match.id} className="border border-slate-300 rounded-lg p-4 bg-white">
              <div className="text-center font-bold text-sm">{ match.group_name }</div>
              <div className="text-center text-xs text-slate-600 mb-3">{ LongDateFormat(match.starts_at) }</div>

              <div className="flex flex-row items-center justify-around">
                <div className="flex flex-col items-center w-1/3">
                  <img src={ match.team_a_badge_thumb || defaultBadgeThumb } alt={ match.team_a_name } className="mb-2" />
                  <span className={`text-sm ${aWins ? "font-bold" : ""}`}>{ match.team_a_name }</span>
                </div>

                <div className="flex flex-row items-center gap-2 text-xl">
                  {showScores ? (
                    <>
                      <span className={`${isLive ? "text-red-500" : ""} ${aWins ? "font-bold" : ""}`}>{ teamAScore }</span>
                      <span>x</span>
                      <span className={`${isLive ? "text-red-500" : ""} ${bWins ? "font-bold" : ""}`}>{ teamBScore }</span>
                    </>
                  ) : (
                    <span>x</span>
                  )}
                </div>

                <div className="flex flex-col items-center w-1/3">
                  <img src={ match.team_b_badge_thumb || defaultBadgeThumb } alt={ match.team_b_name } className="mb-2" />
                  <span className={`text-sm ${bWins ? "font-bold" : ""}`}>{ match.team_b_name }</span>
                </div>
              </div>

              {isLive && (
                <div className="flex justify-center mt-3">
                  <span className="border border-green-500 text-green-500 rounded-full px-3 py-1 text-xs flex items-center gap-1">
                    <span className="animate-pulse">⚽</span> Bola rolando
                  </span>
                </div>
              )}
              {match.is_ended && (
                <div className="flex justify-center mt-3">
                  <span className="border border-green-500 text-green-500 rounded-full px-3 py-1 text-xs flex items-center gap-1">
                    <span>✓</span> Terminou
                  </span>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
  